import Table, { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { Button, Col, Row, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import styled from 'styled-components';
import { styledTable } from '../styled/mixin';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useContext, useMemo } from 'react';
import React from 'react';
import { HolderOutlined } from '@ant-design/icons';
import { Schedule } from '../../modules/schedules';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row2: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined rev={undefined} />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface SubScheduleListComponentProps {
  data: Schedule[];
  openSubScheduleCreateModal: () => void;
  onDeleteSubSchedule: (scheduleId: string) => void;
  updateScheduleIndex: (scheduleId: string, scheduleIndex: number) => void;
}

function SubScheduleListComponent({
  data,
  openSubScheduleCreateModal,
  onDeleteSubSchedule,
  updateScheduleIndex,
}: SubScheduleListComponentProps) {
  const columns: ColumnsType<Schedule> = [
    {
      title: '',
      dataIndex: 'index',
      ellipsis: true,
      align: 'center',
      render: (text: any, record: any) => <DragHandle />,
      width: '3%',
    },
    {
      title: '이름',
      dataIndex: 'name',
      ellipsis: true,
      width: '20%',
      render: (text: any, record: any) => (
        <>
          {/* <span
            className="clickable-table-item"
            onClick={() => moveToDetailPage('' + record.id)}
          >
            {text}
          </span> */}
          {text}
        </>
      ),
    },
    {
      title: '진행률',
      dataIndex: 'complete',
      width: '6%',
      render: (text: any, record: any, index: any) => `${record.complete}%`,
    },
    // {
    //   title: '공종',
    //   dataIndex: 'gongjong',
    //   ellipsis: true,
    //   render: (text: any, record: any) => <>{text === null ? '기타' : text}</>,
    //   sorter: (a: Schedule, b: Schedule) =>
    //     a.gongjong === null || b.gongjong === null
    //       ? -1
    //       : a.gongjong.localeCompare(b.gongjong),
    //   width: '7%',
    // },
    {
      title: '시작일',
      dataIndex: 'startDate',
      width: '10%',
      render: (text: any, record: any, index: any) =>
        record.startDate === null
          ? ''
          : moment(record.startDate).format('YYYY-MM-DD'),
    },
    {
      title: '종료일',
      dataIndex: 'finishDate',
      width: '10%',
      render: (text: any, record: any, index: any) =>
        record.finishDate === null
          ? ''
          : moment(record.finishDate).format('YYYY-MM-DD'),
    },
    // {
    //   title: '',
    //   key: 'action',
    //   dataIndex: '',
    //   align: 'center',
    //   width: '5%',
    //   render: (text, record, index) => (
    //     <Space size="small">
    //       <div onClick={(e) => e.stopPropagation()}>
    //         <Popconfirm
    //           title="정말 삭제하시겠습니까?"
    //           data-test={`project-list-item-${index}`}
    //           onConfirm={() => onDeleteSubSchedule('' + record.id)}
    //         >
    //           <Button size="small">삭제</Button>
    //         </Popconfirm>
    //       </div>
    //     </Space>
    //   ),
    // },
  ];

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const INDEX_OFFSET = 1024;
    let newIndex = 0;
    const activeIndex = data.findIndex((record) => record.id === active?.id);
    const overIndex = data.findIndex((record) => record.id === over?.id);

    const arrLen = data.length;
    if (
      activeIndex === -1 ||
      overIndex === -1 ||
      activeIndex === overIndex ||
      activeIndex >= arrLen ||
      overIndex >= arrLen
    ) {
      return;
    }
    if (overIndex === arrLen - 1) {
      // 마지막에 드랍
      newIndex = data[arrLen - 1].index + INDEX_OFFSET;
    } else if (overIndex === 0) {
      // 처음에 드랍
      newIndex = data[0].index / 2;
    } else if (activeIndex < overIndex) {
      // 중간에 드랍
      newIndex = (data[overIndex].index + data[overIndex + 1].index) / 2;
    } else if (activeIndex > overIndex) {
      // 중간에 드랍
      newIndex = (data[overIndex].index + data[overIndex - 1].index) / 2;
    } else {
      return;
    }

    updateScheduleIndex('' + active.id, newIndex);
  };

  return (
    <StyledDiv>
      <Row justify="space-between" style={{ marginTop: '50px' }}>
        <Col>
          <Title level={5}>하위공정</Title>
        </Col>
        <Col>
          <Space>
            <Button size="small" onClick={() => openSubScheduleCreateModal()}>
              추가
            </Button>
          </Space>
        </Col>
      </Row>

      {/* {data && (
        <Table<Schedule>
          rowKey="id"
          columns={columns}
          dataSource={data}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
            position: ['bottomCenter'],
          }}
          locale={{
            triggerDesc: '내림차순으로 정렬하려면 클릭하세요.',
            triggerAsc: '오름차순으로 정렬하려면 클릭하세요.',
            cancelSort: '정렬을 취소하려면 클릭하세요.',
          }}
        />
      )} */}
      {data && (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={data.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table<Schedule>
              rowKey="id"
              // expandable={{
              //   defaultExpandAllRows: true,
              // }}
              // expandable={tableExpandableConfig}
              columns={columns}
              dataSource={data}
              components={data.length !== 0 ? { body: { row: Row2 } } : {}}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
                position: ['bottomCenter'],
              }}
            />
          </SortableContext>
        </DndContext>
      )}
    </StyledDiv>
  );
}
export default SubScheduleListComponent;
